import FormField, { FormFieldPlaceholder, } from '@polestar/component-warehouse-react/components/form-field/FormField';
import Label from '@polestar/component-warehouse-react/components/label/Label';
import OptionsList, { OptionsListSpaceVariants, } from '@polestar/component-warehouse-react/components/options-list/OptionsList';
import styled from '@emotion/styled';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import Spacings from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import React, { forwardRef, useCallback, useMemo, useRef, useState, useEffect, } from 'react';
import { determineSelection, flattenOptions, getValue, getValues, indexByValue, } from './DropdownField.helpers';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import Icon, { IconSizes } from '@polestar/component-warehouse-react/components/icon/Icon';
import Opacities from '@polestar/component-warehouse-react/tokens/opacities/Opacities';
import AttachedPopover from '@polestar/component-warehouse-react/components/attached-popover/AttachedPopover';
import SelectionOverlay from '@polestar/component-warehouse-react/components/selection-overlay/SelectionOverlay';
import useMediaQuery from '@polestar/component-warehouse-react/hooks/useMediaQuery';
import { useFocusTriggers } from '@polestar/component-warehouse-react/hooks/useFocusTriggers';
import useOptionsListToggled from '@polestar/component-warehouse-react/hooks/useOptionsListToggled';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import useCallbackRef from '@polestar/component-warehouse-react/hooks/useCallbackRef';
import { useInternalApp } from '@polestar/component-warehouse-react/contexts/internal';
import { overflowTextOffsetValue } from '@polestar/component-warehouse-react/helpers/style';
import { focusProviderSkipProps } from '@polestar/component-warehouse-react/contexts/focus';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
import { useFocusInitial } from '@polestar/component-warehouse-react/hooks/focus';
import { useInteractionMode } from '@polestar/component-warehouse-react/contexts/interactionMode';
import { preventCancelableDefault } from '@polestar/component-warehouse-react/helpers/event';
const formatValueLabelDefault = (option) => (option ? option.label : '');
const formatMultipleValueLabelsDefault = (options) => (options === null || options === void 0 ? void 0 : options.map((option) => (option ? option.label : '')).join(', ')) || '';
const renderOptionDefault = (option) => option.label || option.value;
const popoverOffset = { top: Spacings.xSmall, bottom: Spacings.xSmall };
const DropdownField = forwardRef(({ style, value, name, disabled, required, label, whitelistedOptionLabels, error, errorMessage, hint, trailing, options, onChange, onBlur, onFocus, onClick, onPopoverToggled, onMouseEnter, onMouseLeave, formatValueLabel = formatValueLabelDefault, formatMultipleValueLabels = formatMultipleValueLabelsDefault, renderOption = renderOptionDefault, fieldProps, popoverWidth, optionsListProps, multiple = false, internal = false, tooltip, type = 'text', determinePopoverVisible, ...props }, ref) => {
    var _a;
    /**
     * Refs
     */
    const internalRef = useRef(null);
    const combinedRef = useCombinedRef(ref, internalRef);
    const [innerRef, dropdownAttachElement] = useCallbackRef();
    /**
     * Constants
     */
    const [changed, setChanged] = useState(false);
    const flatOptions = useMemo(() => flattenOptions(options, multiple), [options, multiple]);
    const selectableOptionsLength = useMemo(() => flatOptions.filter((val) => (val === null || val === void 0 ? void 0 : val.type) !== 'group').length, [flatOptions]);
    const selectedIndexes = useMemo(() => {
        var _a;
        if (value === undefined || value === '')
            return [];
        if (Array.isArray(value)) {
            return value
                .map((val) => { var _a; return indexByValue(flatOptions, (_a = val) !== null && _a !== void 0 ? _a : null); })
                .filter((index) => index > -1);
        }
        const index = indexByValue(flatOptions, (_a = value) !== null && _a !== void 0 ? _a : null);
        return index > -1 ? [index] : [];
    }, [flatOptions, value]);
    const selectedIndex = selectedIndexes.length > 0 ? Math.min(...selectedIndexes) : -1;
    const selectedOptions = useMemo(() => selectedIndexes.map((val) => flatOptions[val]), [flatOptions, selectedIndexes]);
    const memoizedId = useSSRSafeId('dropdown-field', props.id);
    const memoizedListId = `${memoizedId}-list`;
    const labelId = `${memoizedId}-label`;
    const isMobile = ((_a = useMediaQuery()) === null || _a === void 0 ? void 0 : _a.name) === 'mobile';
    const isUnusable = disabled || !options || options.length <= 0;
    const valueLabel = !multiple
        ? formatValueLabel(selectedOptions[0])
        : formatMultipleValueLabels(selectedOptions);
    /**
     * State
     */
    const [optionsVisible, setOptionsVisible] = useState(false);
    const [hovered, setHovered] = useState(false);
    /**
     * Defaults
     */
    const trailingDefault = trailing || (React.createElement(Icon, { icon: optionsVisible ? Icons.chevronUp : Icons.chevronDown, pairedWithText: false, size: IconSizes.small, style: {
            opacity: disabled || optionsVisible || hovered ? Opacities.full : Opacities.medium,
        } }));
    const optionsListRef = useRef(null);
    const { focused, focusBlurEvents } = useFocusTriggers({
        name,
        fakeFocus: optionsVisible,
        onFocus,
        onBlur,
    });
    useOptionsListToggled(optionsVisible, onPopoverToggled);
    const selectKeyboardInput = useCallback(() => {
        if (internalRef === null || internalRef === void 0 ? void 0 : internalRef.current) {
            internalRef.current.focus();
        }
    }, [internalRef]);
    const handleToggle = useCallback((value) => {
        setOptionsVisible(value);
    }, []);
    const handleMouseEnter = useCallback((e) => {
        setHovered(true);
        onMouseEnter === null || onMouseEnter === void 0 ? void 0 : onMouseEnter(e);
    }, [onMouseEnter]);
    const handleMouseLeave = useCallback((e) => {
        setHovered(false);
        onMouseLeave === null || onMouseLeave === void 0 ? void 0 : onMouseLeave(e);
    }, [onMouseLeave]);
    const handleClick = useCallback((e) => {
        var _a;
        selectKeyboardInput();
        if (!disabled) {
            // @ts-ignore
            onClick === null || onClick === void 0 ? void 0 : onClick(e);
            // Event.composed is used to determine if the event is triggered by an "autoFill" action
            if (!optionsVisible && ((_a = e === null || e === void 0 ? void 0 : e.nativeEvent) === null || _a === void 0 ? void 0 : _a.composed)) {
                setOptionsVisible(true);
            }
        }
    }, [selectKeyboardInput, optionsVisible, onClick, disabled]);
    const handleClose = useCallback(() => {
        selectKeyboardInput();
        handleToggle(false);
    }, [handleToggle, selectKeyboardInput]);
    /**
     * Keyboard controls
     */
    const handleKeyDown = useCallback((e) => {
        // Down
        switch (e.key) {
            case 'Tab':
                if (optionsVisible) {
                    preventCancelableDefault(e);
                }
                handleToggle(false);
                break;
            // Select item
            case 'Enter':
            case ' ':
                preventCancelableDefault(e);
                if (!optionsVisible) {
                    handleToggle(true);
                }
                else if (!multiple) {
                    handleToggle(false);
                }
                break;
            case 'Unidentified':
                // this happens when autocompleted
                break;
            default:
                break;
        }
    }, [optionsVisible, handleToggle, multiple]);
    const handleKeyUp = useCallback((e) => {
        // Down
        switch (e.key) {
            // Blur input
            case 'Escape':
                handleToggle(false);
                break;
            // Select item
            case ' ':
            case 'Enter':
                preventCancelableDefault(e);
                break;
            case 'Backspace':
                preventCancelableDefault(e);
                break;
            case 'Tab':
            case 'Shift':
                break;
            case 'Unidentified':
                // this happens when autocompleted
                break;
            default:
                handleToggle(true);
                break;
        }
    }, [handleToggle]);
    const handleMobileKeyUp = useCallback((e) => {
        // Down
        switch (e.key) {
            // Blur input
            case 'Escape':
                handleToggle(false);
                selectKeyboardInput();
                break;
        }
    }, [handleToggle, selectKeyboardInput]);
    const handleSelect = useCallback((value) => {
        setChanged(true);
        const output = multiple
            ? getValues(determineSelection(selectedOptions, value))
            : getValue(value);
        onChange === null || onChange === void 0 ? void 0 : onChange(output, name);
        if (!multiple) {
            selectKeyboardInput();
            handleToggle(false);
        }
    }, [onChange, selectKeyboardInput, handleToggle, multiple, selectedOptions, name]);
    const internalApp = useInternalApp();
    if (internal && !internalApp) {
        // eslint-disable-next-line no-console
        console.warn('DropdownField: you are trying to use a property ("internal") that can only be set with internal projects. Please use InternalDropdownField.');
        internal = false;
    }
    const handleInput = useCallback((e) => {
        var _a, _b, _c;
        if ((e === null || e === void 0 ? void 0 : e.isTrusted) && (e === null || e === void 0 ? void 0 : e.composed) === false) {
            // only open when it is clicked by an user not by an extension (in Playwright isTrusted=true but composed=true too so therefore cancelable added to the check)
            setOptionsVisible(true);
        }
        setChanged(true);
        // use the available (flattened) options to fulfill all options and return only the contents of the "value" property
        // this is important to support autofill correctly
        const value = flatOptions.find((option) => (option === null || option === void 0 ? void 0 : option.label) === e.target.value || (option === null || option === void 0 ? void 0 : option.value) === e.target.value);
        // determine what kind of value is available to handle autofill or normal typing events correctly.
        const providedValue = value !== undefined || (value === null || value === void 0 ? void 0 : value.value) !== undefined;
        if (providedValue && ((_a = e === null || e === void 0 ? void 0 : e.target) === null || _a === void 0 ? void 0 : _a.value) === undefined) {
            // for normal typing use-cases
            onChange === null || onChange === void 0 ? void 0 : onChange(value === null || value === void 0 ? void 0 : value.value, name);
        }
        // trigger onChange for autofill or pasted content which are very specific where in the even the data is sent.
        const noProvidedValue = value === undefined || (value === null || value === void 0 ? void 0 : value.value) === undefined;
        if (noProvidedValue &&
            (e === null || e === void 0 ? void 0 : e.target.value) !== undefined &&
            // only for autocomplete / autofill use-cases
            ((_b = e === null || e === void 0 ? void 0 : e.nativeEvent) === null || _b === void 0 ? void 0 : _b.inputType) === 'insertFromPaste') {
            if ((e === null || e === void 0 ? void 0 : e.nativeEvent) &&
                e.target.value &&
                // let teams determine if the popover should be visible or not
                determinePopoverVisible(e.nativeEvent.inputType, {
                    value: e.target.value,
                    data: e.nativeEvent.data,
                })) {
                setOptionsVisible(true);
            }
            onChange === null || onChange === void 0 ? void 0 : onChange(e.target.value !== undefined && e.target.value !== ''
                ? e.target.value
                : // for autofill use-cases only
                    e.nativeEvent.data, name, { inputType: (_c = e === null || e === void 0 ? void 0 : e.nativeEvent) === null || _c === void 0 ? void 0 : _c.inputType });
        }
    }, [name, onChange, flatOptions, determinePopoverVisible]);
    // Focus initial item in optionslist
    const [attachedPopoverVisible, setAttachedPopoverVisible] = useState(false);
    useFocusInitial(optionsListRef, attachedPopoverVisible || (optionsVisible && isMobile), {
        preventScroll: false,
        scrollIntoView: false,
    });
    const handleAttachedPopoverVisible = useCallback((visible) => {
        setAttachedPopoverVisible(visible);
    }, []);
    const minimumDimensions = useMemo(() => ({ width: popoverWidth }), [popoverWidth]);
    // prevent that only the last selected item is in focus
    const preventFocusScroll = changed ? selectableOptionsLength === selectedOptions.length : true;
    // make it possible to select with the keyboard after clicking it open
    const { setForcedKeyboardFocus } = useInteractionMode();
    useEffect(() => {
        setForcedKeyboardFocus === null || setForcedKeyboardFocus === void 0 ? void 0 : setForcedKeyboardFocus(optionsVisible);
    }, [optionsVisible, setForcedKeyboardFocus]);
    const keyboardInput = (React.createElement(KeyboardInput, { ...aria({
            autocomplete: 'list',
            invalid: !!errorMessage,
            describedby: optionsVisible && hint ? `${memoizedId}-message` : undefined,
            expanded: optionsVisible,
            role: 'combobox',
            owns: optionsVisible ? memoizedListId : undefined,
            controls: memoizedListId,
            activedescendant: optionsVisible && selectedIndex !== -1 && selectedIndex !== undefined
                ? `${memoizedListId}-${selectedIndex}`
                : undefined,
        }), autoComplete: "off", ...props, ...focusProviderSkipProps, id: memoizedId, ref: combinedRef, type: type, onKeyUp: handleKeyUp, onKeyDown: handleKeyDown, onInput: handleInput, 
        // ensure that on tablets no virtual keyboard pops up
        inputMode: "none" }));
    return (React.createElement(Wrapper, { style: style },
        React.createElement(FormField, { ...fieldProps, label: label, error: error, errorMessage: errorMessage || (typeof error === 'string' && error), hint: hint, disabled: isUnusable, required: required, innerRef: innerRef, trailing: trailingDefault, forId: memoizedId, onClick: handleClick, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, focused: focused, tooltip: tooltip, ...focusBlurEvents }, disabled ? (valueLabel && valueLabel !== '' ? (React.createElement(ValueLabel, { ...props, disabled: true, noWrap: true, lineClamp: 1, ...aria({ disabled: true }) }, valueLabel)) : (React.createElement(React.Fragment, null,
            React.createElement("span", { "data-type": "test3", style: { flex: 1, display: 'flex' } }),
            keyboardInput))) : (React.createElement(React.Fragment, null,
            selectedOptions.map((option, index) => (React.createElement("input", { ...aria({ hidden: true }), key: `${memoizedId}-${index}`, type: "hidden", name: multiple ? `${name}[]` : name, value: getValue(option), tabIndex: -1, style: { visibility: 'hidden' }, autoComplete: "off", 
                // ensure that on tablets no virtual keyboard pops up
                inputMode: "none" }))),
            keyboardInput,
            React.createElement(LabelWrapper, null, valueLabel && valueLabel !== '' && (React.createElement(ValueLabel, { disabled: disabled, noWrap: true, tabIndex: -1, id: label === undefined ? `${memoizedId}-label` : undefined }, valueLabel || (React.createElement(FormFieldPlaceholder, null, props.placeholder)))))))),
        optionsVisible && !isMobile && (React.createElement(AttachedPopover, { attachTo: dropdownAttachElement, onClose: handleClose, offset: popoverOffset, minimumDimensions: minimumDimensions, onVisibilityChange: handleAttachedPopoverVisible },
            React.createElement(OptionsList, { ref: optionsListRef, ...optionsListProps, id: memoizedListId, labelId: `${labelId} ${memoizedId}-message`, options: options, whitelistedOptionLabels: whitelistedOptionLabels, selectedIndex: selectedIndex, value: selectedOptions, onChange: handleSelect, renderOption: renderOption, multiple: multiple, spacingVariant: OptionsListSpaceVariants.fullWidth, internal: internal, flatOptions: flatOptions, preventFocusScroll: preventFocusScroll }))),
        optionsVisible && isMobile && (React.createElement(SelectionOverlay, { onBack: handleClose, onKeyUp: handleMobileKeyUp },
            React.createElement(OptionsList, { ...optionsListProps, ref: optionsListRef, id: memoizedListId, labelId: `${labelId} ${memoizedId}-message`, options: options, whitelistedOptionLabels: whitelistedOptionLabels, selectedIndex: selectedIndex, value: selectedOptions, onChange: handleSelect, renderOption: renderOption, withBorder: false, multiple: multiple, spacingVariant: OptionsListSpaceVariants.fullWidth, internal: internal, flatOptions: flatOptions, preventFocusScroll: preventFocusScroll })))));
});
DropdownField.displayName = 'DropdownField';
export default DropdownField;
const Wrapper = styled.div `
  position: relative;
`;
const LabelWrapper = styled.div `
  position: relative;
  flex: 1;
  max-width: calc(100% - 2rem);
  -webkit-tap-highlight-color: transparent;
`;
const ValueLabel = styled(Label) `
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  color: ${({ disabled, theme }) => disabled
    ? theme.formField.states.disabled.inputTextColor
    : theme.formField.states.enabled.inputTextColor};
  width: 100%;
  max-width: 100%;
  display: block;
  flex-grow: 1;
  padding: calc(${Spacings.small} + ${overflowTextOffsetValue}) ${Spacings.medium} ${Spacings.small}
    ${Spacings.medium};
`;
const KeyboardInput = styled.input `
  position: absolute;
  opacity: 0;
  z-index: -2;
  width: 100%;
  pointer-events: none;
`;
